// Typed contracts mirrored from the backend Swagger (schema version 28).
// Keep field names identical to the wire format; adapt only where the UI model
// genuinely differs, and do that inside the owning service.

export type ContentLocale = 'en' | 'ru' | 'uz' | 'cyr'
export const CONTENT_LOCALES: ContentLocale[] = ['en', 'ru', 'uz', 'cyr']

export type Translated = {
  name_en: string
  name_ru: string
  name_uz: string
  name_cyr: string
}

/** A flat localized label of a joined record, e.g. `substatus_name_en` … `substatus_name_cyr`. */
export type LocalizedFields<Prefix extends string> = { [Key in `${Prefix}_${ContentLocale}`]: string }

export type TranslatedDescription = {
  description_en: string
  description_ru: string
  description_uz: string
  description_cyr: string
}

export type ActiveStatus = 'active' | 'inactive'

export type ListResponse<T> = {
  count: number
  items: T[]
}

export type Timestamps = {
  created_at: string
  updated_at: string
}

// ---------- errors ----------

export type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'INVALID_JSON'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'ALREADY_EXISTS'
  | 'CONFLICT'
  | 'FAILED_PRECONDITION'
  | 'RATE_LIMITED'
  | 'PAYLOAD_TOO_LARGE'
  | 'INTERNAL'
  | 'UNAVAILABLE'
  | 'NETWORK'
  | 'TIMEOUT'
  | 'CANCELLED'
  | 'COMPANY_NOT_LINKED'
  | 'COMPANY_SWITCH_STALE'

export type ErrorEnvelope = {
  error: { code: ApiErrorCode; message: string; details?: Record<string, unknown> }
}

// ---------- per-user UI settings (/v1/me/settings) ----------

/** One stored settings document; a missing one has value null and version 0. */
export type SettingDocument = { value: unknown; version: number; updated_at: string | null }
export type SettingList = { items: Record<string, SettingDocument> }

// ---------- auth ----------

export type AuthTokens = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  refresh_expires_in: number
}

export type AuthClientType = Translated & { id: string; code: string }
export type AuthRole = Translated & { id: string; code: string }

export type AuthProfile = {
  id: string
  display_name: string
  email: string
  locale: string
  default_branch_id: string
  client_type: AuthClientType
  role: AuthRole
}

export type AuthIdentity = {
  user_id: string
  profile_id: string
  company_id: string
  client_type_id: string
  role_id: string
  session_id: string
  credentials_version: number
}

export type LoginRequest = { identifier: string; password: string; profile_id?: string }
/**
 * A company the current sign-in can switch to (the same login and password
 * matched a user there). available=false: that user was blocked or changed its
 * password since, so switching needs a new sign-in.
 */
export type LinkedCompany = Translated & { id: string; code: string; is_current: boolean; available: boolean }

export type LoginResult = { identity: AuthIdentity; profile: AuthProfile; token: AuthTokens; companies?: LinkedCompany[] }

export type AuthChallengeRequested = {
  challenge_id: string
  expires_in: number
  resend_after_seconds: number
}

export type AuthChallengeVerified = {
  challenge_id: string
  expires_in: number
  verification_token: string
}

// ---------- access ----------

export type ResourceType = 'module' | 'menu' | 'page' | 'table' | 'field' | 'action' | 'api'

export type AccessResource = Translated &
  TranslatedDescription &
  Timestamps & {
    id: string
    parent_id: string
    code: string
    resource_type: ResourceType
    route: string
    icon: string
    sort_order: number
    status: string
    is_system: boolean
    metadata: Record<string, unknown>
  }

export type UserBranch = Translated & { id: string; code: string; is_default: boolean }

/** GET /v1/me/branch-scope: the caller's visible branches for one permission. */
export type BranchScope = { all: boolean; branch_ids: string[] }

export type MeContext = {
  profile: AuthProfile
  experience_code: string
  branches: UserBranch[]
  capabilities: string[]
  resources: AccessResource[]
  field_access: Record<string, Record<string, boolean>>
  /** Shows the company switcher: the sign-in matched users in several companies. */
  can_switch_company?: boolean
  /** A trader's or logist's account: the admin is fixed to this balance. */
  balance_group_id?: string
  /** Shows the platform administration (companies); no company role grants it. */
  is_platform_admin?: boolean
  /** The admin menu tree this profile may see (GET /v1/menu-items is the whole tree). */
  menu: MenuItem[]
  /** Tabs and buttons of pages the role does not show (role «Меню» ticks). */
  hidden_controls: { path: string; key: string }[]
}

/** «Записи»: a record field compared with a user value (user:partner, user:me) or a fixed value (fixed:…). */
export type RecordRule = { field: string; value: string }
/** A role's rule for one table: its conditions joined with or (see any) / and (narrow). */
export type RoleRecordRules = { table: string; join: 'or' | 'and'; rules: RecordRule[] }
export type RecordOption = Translated & { value: string }
export type RecordField = Translated & { code: string; user_values: string[]; fixed: RecordOption[] }
export type RecordTable = Translated & { code: string; fields: RecordField[]; named: (Translated & { rule: RecordRule })[] }

/** One node of the admin menu: an area (top level), a folder, a page, or a tab or button (action) of a page. */
export type MenuItem = {
  id: string
  parent_id: string
  code: string
  kind: 'area' | 'folder' | 'page' | 'window' | 'tab' | 'action'
  path: string
  /** A tab or button of its page (tab/action only), e.g. «batches», «history». */
  control_key: string
  /** The access resource whose CRUD rights the row grants; its other actions are the row's buttons. */
  resource_code: string
  /** Reads the row's forms pick from, granted with the row. */
  lookup_codes: string[]
  /** Rights listed under the row's «Действия»; empty = its resource's non-CRUD rights. */
  action_codes: string[]
  /** The table whose «Записи» rule the row edits or follows. */
  record_table: string
  search: string
  icon: string
  label_key: string
  name_en: string
  name_ru: string
  name_uz: string
  name_cyr: string
  /** A page is listed when the role holds all of these and, if any are given, one of any_permission_codes. */
  permission_codes: string[]
  any_permission_codes: string[]
  platform_admin: boolean
  in_account_menu: boolean
  sort_order: number
  is_system: boolean
  children: MenuItem[]
}

// ---------- platform companies (/v1/platform/companies, platform admins only) ----------

export type CompanyStatus = 'active' | 'inactive'

export type Company = Translated & Timestamps & { id: string; code: string; status: CompanyStatus }

export type CompanyCreate = Translated & {
  code?: string
  owner: { display_name: string; login: string; phone: string; password: string }
}

export type CompanyPatch = Partial<Translated> & { status?: CompanyStatus }

/** POST /v1/platform/companies: the company, its first branch and the owner (never the password). */
export type ProvisionedCompany = {
  company: Company
  branch_id: string
  owner: { user_id: string; profile_id: string; role_id: string; display_name: string; login: string; phone: string }
}

/** GET /v1/me/profile — only the part the web shell reads (the header photo). */
export type MeProfile = {
  id: string
  display_name: string
  /** Empty when the profile has no photo. */
  avatar_url: string
}

export type PermissionCatalogItem = Translated &
  TranslatedDescription & {
    id: string
    code: string
    action: string
    resource_id: string
    resource_code: string
    resource_type: ResourceType
    /** The backend narrows this permission by branch, so the grant scope matters. */
    branch_scoped: boolean
    /** Shows money, cost or profit, or changes access. */
    sensitive: boolean
    status: string
    is_system: boolean
  }

export type MobileExperience = Translated &
  TranslatedDescription & {
    id: string
    company_id: string
    code: string
    status: string
    is_system: boolean
  }

export type ClientType = Translated &
  TranslatedDescription &
  Timestamps & {
    guid: string
    code: string
    status: string
    is_system: boolean
    /** The client type (Users page tab) this one belongs to; empty for a tab itself. */
    parent_id?: string
    /** The fixed mobile app its profiles enter; empty when it has none. */
    experience_code?: string
  }

/** The client type a role belongs to: its Users page tab (group_id) and mobile app (experience_code). */
export type RoleClientType = Translated & {
  id: string
  code: string
  group_id: string
  experience_code: string
}

export type GrantEffect = 'allow' | 'deny'
export type GrantScope = 'company' | 'all_branches' | 'assigned_branches' | 'own_record'
export const GRANT_SCOPES: GrantScope[] = ['company', 'all_branches', 'assigned_branches', 'own_record']

export type RolePermission = {
  guid: string
  permission_id: string
  permission_code: string
  permission_name_en: string
  permission_name_ru: string
  permission_name_uz: string
  permission_name_cyr: string
  resource_id: string
  resource_code: string
  resource_name_en: string
  resource_name_ru: string
  resource_name_uz: string
  resource_name_cyr: string
  action: string
  branch_scoped: boolean
  sensitive: boolean
  effect: GrantEffect
  scope_code: GrantScope
  conditions: Record<string, unknown>
}

export type Role = Translated &
  TranslatedDescription &
  Timestamps & {
    guid: string
    code: string
    status: string
    is_system: boolean
    permission_count: number
    permissions: RolePermission[]
    created_by_profile_id: string
    /** The role this one was copied from, once; empty for a role made from scratch. */
    copied_from_role_id: string
    /** Fixed on create: the client type, hence the mobile app, of the role's users. */
    client_type?: RoleClientType
    /** The fixed server behaviour the role follows (e.g. employee.moderator for «Модератор 2»). */
    base_code?: string
  }

export type RoleGrant = {
  permission_id: string
  effect: GrantEffect
  scope_code: GrantScope
  conditions?: Record<string, unknown>
}

/** GET /v1/roles/{id}/history: one saved change set of a role's grants. */
export type RoleHistoryGrant = { effect: GrantEffect; scope_code: GrantScope; conditions: Record<string, unknown> }
export type RoleHistoryChange = {
  permission_id: string
  permission_code: string
  permission_name_en: string
  permission_name_ru: string
  permission_name_uz: string
  permission_name_cyr: string
  change: 'added' | 'removed' | 'changed'
  old: RoleHistoryGrant | null
  new: RoleHistoryGrant | null
  /** A menu change (menu_replaced) names the page instead of a permission. */
  menu_code?: string
  menu_name_en?: string
  menu_name_ru?: string
  menu_name_uz?: string
  menu_name_cyr?: string
}
export type RoleHistoryEvent = {
  id: string
  event_type: 'grants_replaced' | 'role_copied' | 'menu_replaced'
  source_role_id: string
  actor_name: string
  actor_role_name_en: string
  actor_role_name_ru: string
  actor_role_name_uz: string
  actor_role_name_cyr: string
  changes: RoleHistoryChange[]
  created_at: string
}

/** GET /v1/users/{id}/access: what a profile may effectively do. */
export type ProfileAccessScope = 'all_branches' | 'assigned_branches' | 'own_record'
export type ProfileAccessItem = Translated & {
  permission_id: string
  code: string
  resource_id: string
  resource_code: string
  resource_name_en: string
  resource_name_ru: string
  resource_name_uz: string
  resource_name_cyr: string
  action: string
  branch_scoped: boolean
  sensitive: boolean
  scope: ProfileAccessScope
  restricted: boolean
}
export type ProfileAccess = {
  profile_id: string
  display_name: string
  role: AuthRole
  branches: UserBranch[]
  permissions: ProfileAccessItem[]
}

// ---------- administration ----------

export type BranchType = 'main' | 'regional' | 'warehouse' | 'sales' | 'office'

export type Branch = Translated &
  Timestamps & {
    id: string
    company_id: string
    parent_branch_id: string
    location_id: string
    type_code: BranchType
    code: string
    address: string
    timezone: string
    status: ActiveStatus
  }

/** The credential's status, as the backend accepts it (PUT /users/{id}/status). */
export type UserStatus = 'pending' | 'active' | 'blocked' | 'disabled'
/** The profile's status in this company; inactive = «Не активен», cannot sign in. */
export type ProfileStatus = 'pending' | 'active' | 'blocked' | 'inactive'

export type UserIdentity = Timestamps & {
  id: string
  user_id: string
  company_id: string
  login: string
  phone: string
  first_name: string
  middle_name: string
  last_name: string
  display_name: string
  email: string
  avatar_url: string
  locale: string
  user_status: UserStatus
  profile_status: ProfileStatus
  must_reset_password: boolean
  credentials_version: number
  client_type: AuthClientType
  role: AuthRole
  branches: UserBranch[]
  /** Staff number «V382»; empty for staff not backfilled from Ucode yet. */
  public_number: string
  /** The API never returns a password; the list shows it masked from this flag. */
  has_password: boolean
  /** The top client type — the tab the user is listed under. */
  client_type_group_id: string
  /** Mobile app the user opens; empty = none. */
  experience_code: string
  /** The fixed behaviour of the role (e.g. employee.agent for «Агент 2»). */
  role_base_code: string
  /** UZS decimal string (agents). */
  agent_limit: string
  cashbox: { id: string; name: string }
  partner: { id: string; name: string }
  driver_status: DriverStatus | ''
}

export type DriverStatus = 'active' | 'inactive' | 'lunch' | 'repairing' | 'moving'

/** What a role adds to the users screen: form fields, list columns and row actions. */
export type RoleExtraField = 'agent_limit' | 'cashbox_id' | 'partner_id'
export type RoleColumn = 'agent_limit' | 'driver_status' | 'partner'
export type RoleRowAction = 'warehouses' | 'agents' | 'customs_posts'

/** A sub-tab of «Пользователи по ролям»: one active role. */
export type UsersTabRole = Translated & {
  id: string
  code: string
  base_code: string
  is_system: boolean
  client_type_id: string
  experience_code: string
  extra_fields: RoleExtraField[]
  columns: RoleColumn[]
  row_actions: RoleRowAction[]
}

/** A tab of «Пользователи по ролям»: a top client type, its child types and its roles. */
export type UsersTab = {
  client_type: AuthClientType & { experience_code: string }
  client_types: (AuthClientType & { experience_code: string })[]
  roles: UsersTabRole[]
}

export type UserWarehouse = { id: string; name: string; branch_id: string; branch_name: string }

export type BranchAssignment = { branch_id: string; is_default: boolean }

// ---------- catalog ----------

export type Country = Translated & Timestamps & { id: string; code: string; status: ActiveStatus }

export type LocationType = 'city' | 'border_crossing' | 'port' | 'warehouse'
export type Location = Translated &
  Timestamps & { id: string; code: string; country_id: string; type_code: LocationType; status: ActiveStatus }

export type Category = Translated &
  Timestamps & {
    id: string
    parent_id: string
    image_url: string
    sort_order: number
    status: ActiveStatus
  }

export type PackageUnit = 'kg' | 'g' | 'piece'
export type Package = Translated &
  Timestamps & { id: string; weight: string; unit_code: PackageUnit; status: ActiveStatus }

export type Product = Translated &
  TranslatedDescription &
  Timestamps & {
    id: string
    code: string
    category_id: string
    country_id: string
    image_url: string
    is_belarus_meat: boolean
    is_uncut_meat: boolean
    /** «Заморозка»: Belarus meat in quarters received without a truck cell. */
    is_frozen_meat: boolean
    rear_weight: string
    front_weight: string
    carcass_weight: string
    weight_unit_code: 'kg' | 'g'
    sort_order: number
    package_ids: string[]
    status: ActiveStatus
    /** The product's balance (AFF's «A» products); empty = the default one (ChF). */
    balance_group_id?: string
  }

export type ProductGroupItem = { product_id: string; weight: string; percentage: string }
export type ProductGroup = Translated &
  Timestamps & {
    id: string
    code: string
    partner_id: string
    items: ProductGroupItem[]
    total_weight: string
    status: ActiveStatus
  }

// ---------- parties / procurement ----------

export type PartnerType = 'factory' | 'trader' | 'logistic' | 'declarant'

export type Partner = Timestamps & {
  /** The partner's country; products added inside a container's product group come from the factory's (ALFI TEST #29). */
  country_id?: string
  id: string
  company_id: string
  party_id: string
  currency_id: string
  currency_code: string
  code: string
  name: string
  /** «Буква»: the seller's letter in generated batch numbers (empty = first letter of the name). */
  batch_letter: string
  partner_types: PartnerType[]
  tin: string
  phone: string
  email: string
  address: string
  status: ActiveStatus
}

export type LegalEntity = Timestamps & {
  id: string
  company_id: string
  party_id: string
  code: string
  name: string
  /** «Буква»: the buyer's letter in generated batch numbers (empty = first letter of the name). */
  batch_letter: string
  tin: string
  address: string
  status: ActiveStatus
  /** The firm's balance («Балансы»), the default one when it is in none. */
  balance_group_id?: string
  /** «Материнская компания»: an importer balance (AFF or ChF); empty when none. */
  parent_group_id?: string
  parent_group_name?: string
}

export type ProcurementTerm = Translated & Timestamps & {
  id: string
  company_id: string
  code: string
  status: ActiveStatus
}

export type ProcurementUnitType = 'container' | 'truck'

export type ProcurementContainerType = ProcurementTerm & {
  capacity_kg: string
  unit_type: ProcurementUnitType
}

export type ProcurementStatusTemplate = ProcurementTerm & {
  order_number: number
  /** The substatus where a truck accrues customs debt (legacy "active" / Declarant). */
  is_customs_point: boolean
  /** Badge colour in the transport lists: "#rrggbb", "" for the default. */
  color: string
  /** Offered on Proforma stages 2–3 («В пути по воде 1/2»). */
  is_water: boolean
  /** Offered on Proforma stages 4–5 («В пути по суше», «Внутри Узбекистана»). */
  is_land: boolean
}

/**
 * A Batch's containers move together: their common status, one of them
 * standing for it, and the targets they can all move to. `mixed`: they stand
 * on different substatuses and cannot move.
 */
/** A container port expense kind («Расходы контейнеров»). */
export type ProcurementContainerExpenseKind = 'terminal' | 'unloading' | 'storage' | 'loading' | 'documents' | 'margin' | 'extra'

/** One port expense of a batch's container, billed by a sublogist («Компания»). */
export type ProcurementContainerExpense = {
  id: string
  batch_id: string
  transport_unit_id: string
  container_number: string
  kind: ProcurementContainerExpenseKind
  gross_weight: string
  rate_per_ton: string
  days: number
  amount: string
  amount_uzs: string
  partner_id: string
  partner_name: string
  /** «Логист» of the row ('' = none); an outside logist is partner_id too. */
  logist_id: string
  logist_name: string
  currency_id: string
  currency_code: string
  note: string
  posting_id: string
  debt_id: string
  updated_at: string
}

export type ProcurementBatchContainerExpenses = {
  batch_id: string
  /** gross_weight: the container's gross kg, entered once in «Выгрузка на склад» ('' until entered). */
  containers: { id: string; container_number: string; net_weight: string; gross_weight: string }[]
  items: ProcurementContainerExpense[]
  /** Containers without every kind entered yet (0 counts); customs waits for them (client 2026-10-25). */
  missing?: ProcurementContainerExpenseGap[]
}

/** A container's kinds with no row yet, in display order. */
export type ProcurementContainerExpenseGap = { transport_unit_id: string; container_number: string; kinds: ProcurementContainerExpenseKind[] }

/** A row to save; its gross kg is its container's (ProcurementContainerExpensesInput.containers). */
export type ProcurementContainerExpenseInput = {
  transport_unit_id: string
  kind: ProcurementContainerExpenseKind
  rate_per_ton: string
  days: number
  amount: string
  /** «Компания», the sublogist; '' with an outside logist. */
  partner_id: string
  /** «Логист» (the stages' rule, ALFI TEST #29); '' = none. */
  logist_id: string
  currency_id: string
  note: string
}

/** «Расходы контейнеров» to save: the rows and each container's gross kg. */
export type ProcurementContainerExpensesInput = {
  items: ProcurementContainerExpenseInput[]
  containers: { transport_unit_id: string; gross_weight: string }[]
}

export type ProcurementBatchStatus = {
  container_count: number
  mixed: boolean
  transport_unit: ProcurementTransportUnit | null
  transitions: ProcurementTransportAllowedTransition[]
}

export type ProcurementItemType = 'product' | 'product_group'

export type ProcurementPlanItem = {
  id: string
  item_type: ProcurementItemType
  product_id: string
  product_group_id: string
  container_count: string
}

export type ProcurementPlan = Timestamps & {
  id: string
  company_id: string
  plan_number: string
  created_date: string
  start_date: string
  end_date: string
  note: string
  status: 'draft' | 'active' | 'closed' | 'cancelled'
  version: number
  item_count: number
  items: ProcurementPlanItem[]
}

export type ProcurementProformaItem = {
  id: string
  item_type: ProcurementItemType
  product_id: string
  product_group_id: string
  container_type_id: string
  weight: string
  container_count: string
  available_weight: string
  available_container_count: string
  unit_price: string
  total_price: string
  note: string
  /** «Цена завода» per kg; empty without a trader. */
  factory_price: string
}

export type ProcurementProformaStageTemplate = Translated & {
  id: string
  status_template_id: string
  order_number: number
}

/** A stage has no configurable name; the server names the five fixed stages by route order. */
export type ProcurementProformaStage = Translated & {
  id: string
  stage_code: string
  order_number: number
  percentage: string
  sub_statuses: ProcurementProformaStageTemplate[]
  /** Invoiced and used (accrual, a unit at or past it, or a goods payment): the server refuses a change. */
  locked?: boolean
}

export type ProcurementProforma = Timestamps & LocalizedFields<'payment_term_name'> & {
  id: string
  company_id: string
  proforma_number: string
  proforma_name: string
  seller_document_number: string
  proforma_date: string
  proforma_type: ProcurementUnitType
  procurement_plan_id: string
  procurement_plan_name: string
  seller_id: string
  seller_name: string
  factory_id: string
  factory_name: string
  buyer_id: string
  buyer_name: string
  declarant_id: string
  declarant_name: string
  payment_term_id: string
  payment_term_name: string
  currency_id: string
  currency_code: string
  total_container_count: string
  total_weight: string
  total_price: string
  note: string
  status: 'draft' | 'approved' | 'cancelled'
  /** «Дата оплаты» of the first status (YYYY-MM-DD or ''): a calendar reminder only. */
  first_stage_payment_date?: string
  has_invoice: boolean
  /** The prices reached the books or the warehouse: an invoiced line's prices are fixed. */
  prices_locked?: boolean
  has_available_items: boolean
  items_locked: boolean
  version: number
  item_count: number
  items: ProcurementProformaItem[]
  statuses: ProcurementProformaStage[]
}

export type ProcurementProformaItemLock = {
  items_locked: boolean
  allocated_item_count: number
  proforma_type: ProcurementUnitType
  transport_unit_type: string
}

export type ProcurementBatchItem = LocalizedFields<'item_name'> & LocalizedFields<'container_type_name'> & {
  id: string
  proforma_item_id: string
  item_type: ProcurementItemType
  product_id: string
  product_group_id: string
  item_name: string
  container_type_id: string
  weight: string
  container_count: string
  available_weight: string
  available_container_count: string
  unit_price: string
  total_price: string
  line_order: number
  note: string
}

export type ProcurementBatchCurrencyRate = {
  id: string
  currency_id: string
  currency_code: string
  rate_to_uzs: string
  exchange_rate_version_id: string
  exchange_rate_item_id: string
  effective_from: string
  snapshotted_at: string
}

/** The dates a container batch's containers share (water ways, planned date per stage). */
export type ProcurementBatchLogistics = {
  water_way_1_arrival_date: string
  water_way_1_days: number | null
  water_way_1_actual_date: string
  water_way_2_arrival_date: string
  water_way_2_days: number | null
  water_way_2_actual_date: string
  /** Stage 1 «Готов на заводе», dated like a water way; absent on batches saved before it. */
  factory_arrival_date?: string
  factory_days?: number | null
  factory_actual_date?: string
  status_plans: { proforma_status_id: string; planned_date: string }[]
}

export type ProcurementBatchStageCharge = {
  id: string
  proforma_status_id: string
  stage_code: string
  order_number: number
  carrier_id: string
  carrier_name: string
  currency_id: string
  currency_code: string
  amount: string
  /** «Наличные»: the cash part of amount («Итого»). */
  /** «Сумма логиста»; empty = amount. */
  logist_amount?: string
  /** «Логист»: the partner over the carrier («Сублогист»), in its currency; empty = none. */
  logist_id?: string
  cash_amount?: string
  /** «Перечисление»: amount - cash_amount. */
  transfer_amount?: string
  is_checked: boolean
  activated_at?: string
  posted_at?: string
}

export type ProcurementBatch = Timestamps & {
  id: string
  company_id: string
  branch_id: string
  proforma_id: string
  proforma_number: string
  proforma_name: string
  proforma_type: ProcurementUnitType
  batch_number: string
  invoice_number: string
  batch_date: string
  status: 'active' | 'closed' | 'cancelled'
  /** «Подстатус»: the earliest current substatus of the batch's transport units ('' without units). */
  substatus_id?: string
  substatus_name_en?: string
  substatus_name_ru?: string
  substatus_name_uz?: string
  substatus_name_cyr?: string
  currency_id: string
  currency_code: string
  seller_id: string
  seller_name: string
  factory_id: string
  factory_name: string
  buyer_id: string
  buyer_name: string
  customs_rate: string
  /** The sea waybill's document number («Bill of lading»). */
  bill_of_lading?: string
  /** «Дата коносамента» ('' none), «Дней» and «Окончание периода» (= date + days, from the server). */
  bill_of_lading_date?: string
  bill_of_lading_days?: number
  bill_of_lading_end_date?: string
  total_weight: string
  total_price: string
  container_count: number
  truck_count: number
  note: string
  version: number
  created_by_profile_id: string
  items: ProcurementBatchItem[]
  currency_rates: ProcurementBatchCurrencyRate[]
  stage_charges: ProcurementBatchStageCharge[]
  /** The dates its containers share; only on a single batch read. */
  logistics?: ProcurementBatchLogistics
}

export type ProcurementTransportUnitItem = LocalizedFields<'product_name'> &
  LocalizedFields<'product_group_name'> &
  LocalizedFields<'package_name'> & {
  id: string
  batch_id: string
  batch_number: string
  batch_item_id: string
  /** Absent from responses of backends before Ucode allocation. */
  proforma_item_id?: string
  source_item_type: ProcurementItemType
  product_id: string
  product_name: string
  product_group_id: string
  product_group_name: string
  package_id: string
  package_name: string
  original_weight: string
  current_weight: string
  unit_price: string
  original_amount: string
  current_amount: string
  package_count: string
  current_package_count: string
  allocated_container_count: string
  front_count: string
  rear_count: string
  note: string
  /** The line's own Proforma, invoice and currency (a «Перевалка» truck may carry several Proformas). */
  proforma_id?: string
  proforma_number?: string
  invoice_number?: string
  currency_code?: string
  /** For goods moved from a container («Перевалка»), that container's number. */
  origin_container_number?: string
}

/** One trailer («Прицеп») of a truck: several are kept, exactly one active (= trailer_number). */
export type ProcurementTransportUnitTrailer = {
  id: string
  trailer_number: string
  is_active: boolean
}

export type ProcurementTransportUnitHead = {
  id: string
  carrier_id: string
  carrier_name: string
  vehicle_number: string
  driver_name: string
  note: string
  is_active: boolean
  assigned_at: string
  released_at?: string
}

export type ProcurementTransportUnitStageCharge = LocalizedFields<'stage_name'> & {
  id: string
  batch_id: string
  source_batch_stage_charge_id: string
  proforma_status_id: string
  stage_code: string
  carrier_id: string
  carrier_name: string
  currency_id: string
  currency_code: string
  amount: string
  /** «Наличные»: the cash part of amount («Итого»). */
  cash_amount?: string
  /** «Перечисление»: amount - cash_amount, the only logistics in the customs base. */
  transfer_amount?: string
  /** «Сумма логиста»: what the logist bills the trader; empty = amount. */
  logist_amount?: string
  /** «Логист»: the partner over the carrier («Сублогист»), in its currency; empty = none. */
  logist_id?: string
  amount_uzs?: string
  status: string
  note: string
  finance_posting_id?: string
  procurement_debt_id?: string
  finance_reversal_posting_id?: string
  procurement_debt_reversal_id?: string
  version: number
  activated_at?: string
  posted_at?: string
  reversed_at?: string
}

export type ProcurementTransportUnit = Timestamps &
  LocalizedFields<'container_type_name'> &
  LocalizedFields<'location_name'> &
  LocalizedFields<'status_name'> &
  LocalizedFields<'substatus_name'> & {
  id: string
  company_id: string
  branch_id: string
  proforma_id: string
  proforma_number: string
  proforma_name: string
  unit_type: ProcurementUnitType
  source_type: 'direct' | 'movement'
  land_vehicle_type: 'truck' | 'wagon' | ''
  container_number: string
  container_type_id: string
  container_type_name: string
  trailer_number: string
  location_id: string
  location_name: string
  current_proforma_status_id: string
  stage_code: string
  status_name: string
  current_proforma_substatus_id: string
  substatus_name: string
  status_template_id: string
  /** The current substatus's badge colour ("#rrggbb", "" for the default). */
  substatus_color: string
  /** The unit's Batch (a mixed truck's first); a container has one. */
  batch_id: string
  /** At the last substatus of its stage. */
  is_last_in_stage?: boolean
  /** At the last substatus of its whole route in its Proforma: only then can a container be moved into a truck. */
  is_route_end?: boolean
  /** The Proforma's stage 4 and 5 substatuses: containers share a truck only with the same. */
  land_route?: string
  content_status: string
  released_at?: string
  /** The container's «Релиз» switch, only kept for now. */
  release_flag?: boolean
  transport_date: string
  total_weight: string
  total_amount: string
  /** What the unit was loaded with; absent before the Ucode list fields. */
  loaded_weight?: string
  loaded_amount?: string
  container_capacity_kg?: string
  currency_id: string
  currency_code: string
  water_way_1_arrival_date?: string
  water_way_1_transshipment_date?: string
  water_way_1_days?: number
  water_way_2_arrival_date?: string
  water_way_2_transshipment_date?: string
  water_way_2_days?: number
  water_way_1_actual_date?: string
  water_way_2_actual_date?: string
  factory_arrival_date?: string
  factory_transshipment_date?: string
  factory_days?: number
  factory_actual_date?: string
  note: string
  version: number
  batch_count: number
  content_count: number
  head_count: number
  batch_numbers: string
  invoice_numbers: string
  active_vehicle_number: string
  active_carrier_id: string
  active_carrier_name: string
  created_by_profile_id: string
  items: ProcurementTransportUnitItem[]
  heads: ProcurementTransportUnitHead[]
  /** Only on the detail read of a truck; trailer_number is the active one. */
  trailers?: ProcurementTransportUnitTrailer[]
  stage_charges: ProcurementTransportUnitStageCharge[]
  /** Main statuses with planned and actual completion dates; only on the detail read. */
  status_plans?: ProcurementTransportUnitStatusPlan[]
}

export type ProcurementTransportUnitHistory = {
  id: string
  transport_unit_id: string
  actor_profile_id: string
  actor_name: string
  actor_role_name_en: string
  actor_role_name_ru: string
  actor_role_name_uz: string
  actor_role_name_cyr: string
  event_type: string
  reason: string
  version: number
  before_snapshot?: Record<string, unknown>
  after_snapshot?: Record<string, unknown>
  created_at: string
}

export type ProcurementTransportUnitDeleteCheck = {
  can_delete: boolean
  reasons: string[]
}

/** name_en … name_cyr are filled only for configurable labels (statuses, container types, locations). */
export type ProcurementTransportFilterOption = Translated & { id: string; code: string; name: string }

export type ProcurementTransportFilterOptions = {
  unit_types: ProcurementTransportFilterOption[]
  source_types: ProcurementTransportFilterOption[]
  content_statuses: ProcurementTransportFilterOption[]
  proformas: ProcurementTransportFilterOption[]
  statuses: ProcurementTransportFilterOption[]
  substatuses: ProcurementTransportFilterOption[]
  container_types: ProcurementTransportFilterOption[]
  locations: ProcurementTransportFilterOption[]
  carriers: ProcurementTransportFilterOption[]
}

export type ProcurementTransportAllowedTransition = LocalizedFields<'status_name'> & LocalizedFields<'substatus_name'> & {
  proforma_status_id: string
  stage_code: string
  status_name: string
  proforma_substatus_id: string
  substatus_name: string
  order_number: number
  substatus_order_number: number
  direction: 'forward' | 'reverse'
  /** How many substatuses the jump passes; 1 is the adjacent one. */
  steps: number
  requires_finance: boolean
  is_blocked: boolean
  block_code: '' | ProcurementTransportJumpBlock
  block_reason: string
  /**
   * customs_paid: the Batches whose customs declaration is paid on the «Декларация» page;
   * container_expenses_missing: the Batches whose «Расходы контейнеров» are not all entered (past the customs point).
   */
  block_batches: string[]
}

export type ProcurementTransportJumpBlock =
  | 'received'
  | 'no_permission'
  | 'no_reverse_permission'
  | 'container_moved'
  | 'customs_paid'
  | 'container_expenses_missing'

/** One substatus of a unit's route (its type's stages, in order). */
export type ProcurementTransportRouteStop = LocalizedFields<'status_name'> & LocalizedFields<'substatus_name'> & {
  proforma_status_id: string
  stage_code: string
  order_number: number
  percentage: string
  proforma_substatus_id: string
  status_template_id: string
  substatus_order_number: number
  is_customs_point: boolean
  is_last_in_stage: boolean
  /** At the last substatus of the unit's whole route in its Proforma: a container may go into a truck. */
  is_route_end: boolean
  is_current: boolean
}

/** A debt one jump step accrues or reverses; a reversal reports the payee's payable balance after it. */
export type ProcurementTransportJumpEffect = {
  action: 'accrue' | 'reverse'
  debt_type: 'goods' | 'logistics' | 'customs'
  batch_id: string
  batch_number: string
  invoice_number: string
  primary_transport_label: string
  party_name: string
  currency_id: string
  currency_code: string
  amount: string
  amount_base: string
  exchange_rate_to_base: string
  article: string
  customs_goods_value: string | null
  customs_logistics_value: string | null
  /** A reversed logistics charge returns to planned with the same amount. */
  replanned: boolean
  payable_balance: string
  overpayment: string
}

export type ProcurementTransportJumpStep = {
  number: number
  from: ProcurementTransportRouteStop
  to: ProcurementTransportRouteStop
  effects: ProcurementTransportJumpEffect[]
  /** Unpaid customs declarations a truck moving back over the customs point removes. */
  deleted_declarations: { id: string; batch_id: string; batch_number: string }[] | null
}

/** What a status jump will do, computed by the server in a rolled-back run of the same jump. */
export type ProcurementTransportJumpPreview = {
  transport_unit_id: string
  version: number
  direction: 'forward' | 'reverse'
  requires_reason: boolean
  from: ProcurementTransportRouteStop
  to: ProcurementTransportRouteStop
  steps: ProcurementTransportJumpStep[]
}

/** product_name … target_package_name are snapshots; the localized names come from the current catalog. */
export type ProcurementMovementItem = LocalizedFields<'product_name'> &
  LocalizedFields<'product_group_name'> &
  LocalizedFields<'source_package_name'> &
  LocalizedFields<'target_package_name'> & {
  id: string
  source_transport_unit_id: string
  source_transport_content_id: string
  target_transport_content_id: string
  batch_id: string
  batch_item_id: string
  product_id: string
  product_group_id: string
  source_package_id: string
  target_package_id: string
  source_weight_before: string
  moved_weight: string
  remaining_weight: string
  source_amount_before: string
  moved_amount: string
  remaining_amount: string
  source_package_count_before: string
  moved_package_count: string
  remaining_package_count: string
  source_front_count_before: string
  moved_front_count: string
  remaining_front_count: string
  source_rear_count_before: string
  moved_rear_count: string
  remaining_rear_count: string
  allocated_container_count_before: string
  moved_allocated_container_count: string
  remaining_allocated_container_count: string
  product_name: string
  product_group_name: string
  batch_number: string
  proforma_number: string
  source_container_number: string
  currency_code: string
  source_package_name: string
  target_package_name: string
  source_package_weight: string
  target_package_weight: string
  is_belarus_meat: boolean
  is_uncut_meat: boolean
  product_front_weight: string
  product_rear_weight: string
  product_carcass_weight: string
  source_package_unit_code: string
  target_package_unit_code: string
  product_weight_unit_code: string
}

export type ProcurementMovementHead = {
  id: string
  transport_head_id: string
  carrier_id: string
  carrier_name: string
  vehicle_number: string
  driver_name: string
  assigned_at: string
  is_active: boolean
  note: string
}

export type ProcurementMovement = LocalizedFields<'location_name'> &
  LocalizedFields<'target_status_name'> &
  LocalizedFields<'target_substatus_name'> & {
  id: string
  company_id: string
  branch_id: string
  proforma_id: string
  proforma_number: string
  proforma_name: string
  target_transport_unit_id: string
  movement_date: string
  location_id: string
  location_name: string
  currency_id: string
  currency_code: string
  trailer_number: string
  land_vehicle_type: 'truck' | 'wagon'
  /** The wagon Перевалка this wagon belongs to ("" for a truck); its wagons move together. */
  wagon_group_id: string
  /** Every wagon number of the wagon Перевалка. */
  wagon_numbers: string
  vehicle_number: string
  carrier_id: string
  carrier_name: string
  target_stage_code: string
  target_substatus_name: string
  /** The truck's substatus badge colour ("#rrggbb", "" for the default). */
  target_substatus_color: string
  total_weight: string
  total_amount: string
  total_package_count: string
  total_front_count: string
  total_rear_count: string
  source_unit_count: number
  item_count: number
  batch_numbers: string
  source_container_numbers: string
  note: string
  idempotency_key: string
  created_by_profile_id: string
  items: ProcurementMovementItem[]
  head: ProcurementMovementHead | null
  target_transport_unit: ProcurementTransportUnit | null
  stage_charges: ProcurementTransportUnitStageCharge[]
  created_at: string
  /** Detail only: editable while the truck stands where the movement put it and nothing is accrued for it. */
  editable: boolean
  /** Detail only: status_changed | stage_charge_posted | debt_accrued | customs_paid | received */
  lock_reasons: string[]
}

export type ProcurementMovementFilterOption = Translated & { id: string; code: string; name: string }

export type ProcurementMovementFilterOptions = {
  proformas: ProcurementMovementFilterOption[]
  locations: ProcurementMovementFilterOption[]
  carriers: ProcurementMovementFilterOption[]
  vehicle_types: ProcurementMovementFilterOption[]
  currencies: ProcurementMovementFilterOption[]
  target_statuses: ProcurementMovementFilterOption[]
}

export type ProcurementCustomsDeclarationItem = LocalizedFields<'product_name'> &
  LocalizedFields<'product_group_name'> &
  LocalizedFields<'package_name'> & {
  id: string
  transport_content_id: string
  product_id: string
  product_name: string
  product_group_id: string
  product_group_name: string
  package_id: string
  package_name: string
  weight: string
  package_count: string
  front_count: string
  rear_count: string
  unit_price: string
  total_amount: string
}

export type ProcurementCustomsAttachment = {
  id: string
  url: string
  name: string
  sort_order: number
  created_at: string
}

export type ProcurementCustomsDeclaration = Timestamps &
  LocalizedFields<'country_name'> &
  LocalizedFields<'customs_location_name'> &
  LocalizedFields<'post_name'> & {
  id: string
  company_id: string
  branch_id: string
  batch_id: string
  batch_number: string
  invoice_number: string
  transport_unit_id: string
  unit_type: ProcurementUnitType
  container_number: string
  proforma_id: string
  proforma_number: string
  country_id: string
  country_name: string
  customs_location_id: string
  customs_location_name: string
  /** The customs post as shown: the declaration's customs location, else the truck's location. Its accounts pay it. */
  post_id: string
  post_name: string
  currency_id: string
  currency_code: string
  factory_id: string
  factory_name: string
  seller_id: string
  seller_name: string
  buyer_legal_entity_id: string
  buyer_legal_entity_name: string
  transport_head_id: string
  vehicle_number: string
  trailer_number: string
  /** Follows the payment: cleared («Растаможен») while its own payment exists, pending («Не растаможен») otherwise. */
  status: 'pending' | 'cleared'
  amount: string
  declaration_number: string
  declaration_at?: string
  border_crossed_at?: string
  note: string
  finance_posting_id?: string
  procurement_debt_id?: string
  finance_reversal_posting_id?: string
  procurement_debt_reversal_id?: string
  version: number
  item_count: number
  attachment_count: number
  /** Customs debt accrued for this post in UZS; absent before the Ucode list field. */
  accrual_base?: string
  /** «Таможенный счёт» the declaration is paid from; it sets the currency of the amount. */
  customs_account_id: string
  customs_account_name: string
  /** Finance payment created by clearing; empty until cleared. */
  payment_operation_id: string
  created_by_profile_id: string
  items: ProcurementCustomsDeclarationItem[]
  attachments: ProcurementCustomsAttachment[]
}

export type ProcurementCustomsAccount = LocalizedFields<'location_name'> & {
  id: string
  name: string
  account_number: string
  currency_id: string
  currency_code: string
  legal_entity_id: string
  legal_entity_name: string
  /** The customs post the account pays at; empty = none. */
  location_id: string
  balance: string
}

/** A customs post assigned to a user (limits what they see without the all-posts right). */
export type ProfileCustomsPost = Translated & { id: string; code: string }

export type ProcurementCustomsAmount = { currency_code: string; amount: string }

export type ProcurementCustomsTurnoverLine = {
  operation_id: string
  operation_number: number
  occurred_at: string
  invoice_number: string
  batch_number: string
  sender_name: string
  receiver_name: string
  vehicle_number: string
  trailer_number: string
  currency_code: string
  income: string
  expense: string
  balance: string
  legal_entity_name: string
  account_name: string
  comment: string
}

export type ProcurementCustomsTurnover = {
  opening: ProcurementCustomsAmount[]
  lines: ProcurementCustomsTurnoverLine[]
  total_income: ProcurementCustomsAmount[]
  total_expense: ProcurementCustomsAmount[]
  closing: ProcurementCustomsAmount[]
}

export type ProcurementCustomsDeclarationHistory = {
  id: string
  declaration_id: string
  actor_profile_id: string
  event_type: string
  reason: string
  version: number
  before_snapshot?: Record<string, unknown>
  after_snapshot?: Record<string, unknown>
  created_at: string
}

export type ProcurementCustomsDeleteCheck = { can_delete: boolean; reasons: string[] }
export type ProcurementCustomsFilterOption = Translated & { id: string; code: string; name: string }

export type ProcurementCustomsFilterOptions = {
  statuses: ProcurementCustomsFilterOption[]
  batches: ProcurementCustomsFilterOption[]
  transport_units: ProcurementCustomsFilterOption[]
  proformas: ProcurementCustomsFilterOption[]
  countries: ProcurementCustomsFilterOption[]
  customs_locations: ProcurementCustomsFilterOption[]
  currencies: ProcurementCustomsFilterOption[]
  factories: ProcurementCustomsFilterOption[]
  sellers: ProcurementCustomsFilterOption[]
  buyer_legal_entities: ProcurementCustomsFilterOption[]
}

export type ProcurementCustomsEligibleOption = LocalizedFields<'current_stage_name'> &
  LocalizedFields<'current_substatus_name'> & {
  batch_id: string
  batch_number: string
  invoice_number: string
  proforma_id: string
  proforma_number: string
  proforma_name: string
  transport_unit_id: string
  unit_type: ProcurementUnitType
  container_number: string
  trailer_number: string
  vehicle_number: string
  current_stage_code: string
  current_substatus_name: string
  remaining_content_count: number
  remaining_content_weight: string
  currency_id: string
  currency_code: string
  factory_id: string
  factory_name: string
  seller_id: string
  seller_name: string
  buyer_legal_entity_id: string
  buyer_legal_entity_name: string
  has_active_declaration: boolean
}

export type ProcurementDebt = {
  id: string
  company_id: string
  branch_id: string
  posting_id: string
  entry_kind: 'accrual' | 'reversal'
  original_accrual_id?: string
  debt_type: 'goods' | 'logistics' | 'customs'
  transport_stage_charge_id?: string
  customs_declaration_id?: string
  proforma_status_id?: string
  proforma_id: string
  proforma_number: string
  batch_id: string
  batch_number: string
  primary_transport_unit_id: string
  secondary_transport_unit_id?: string
  seller_id?: string
  seller_name: string
  buyer_legal_entity_id: string
  buyer_legal_entity_name: string
  currency_id: string
  currency_code: string
  entry_date: string
  amount: string
  signed_amount: string
  amount_base: string
  signed_amount_base: string
  exchange_rate_to_base: string
  invoice_number: string
  article: string
  note: string
  reason: string
  actor_profile_id?: string
  created_at: string
  /** Container or trailer numbers; absent from backends before the Ucode debt list. */
  primary_transport_label?: string
  secondary_transport_label?: string
}

export type ProcurementDebtSummary = {
  seller_id?: string
  seller_name: string
  buyer_legal_entity_id: string
  buyer_legal_entity_name: string
  currency_id: string
  currency_code: string
  accrual_amount: string
  reversal_amount: string
  net_amount: string
  accrual_amount_base: string
  reversal_amount_base: string
  net_amount_base: string
}

export type ProcurementDebtFilterOption = { id?: string; code?: string; name: string }

export type ProcurementDebtFilterOptions = {
  entry_kinds: ProcurementDebtFilterOption[]
  debt_types: ProcurementDebtFilterOption[]
  sellers: ProcurementDebtFilterOption[]
  buyer_legal_entities: ProcurementDebtFilterOption[]
  currencies: ProcurementDebtFilterOption[]
  proformas: ProcurementDebtFilterOption[]
  batches: ProcurementDebtFilterOption[]
  transport_units: ProcurementDebtFilterOption[]
}

export type ProcurementReportColumnType = 'text' | 'number' | 'datetime'
export type ProcurementReportFilterKind = 'entity' | 'enum' | 'date'
export type ProcurementReportRowType = 'data' | 'section' | 'subtotal' | 'total'

export type ProcurementReportColumn = {
  key: string
  label: string
  type: ProcurementReportColumnType
}

export type ProcurementReportFilter = {
  key: string
  label: string
  kind: ProcurementReportFilterKind
  multiple: boolean
  /** A single-choice switch's value when none is sent («Баланс товаров» «В пути» = "with"). */
  default?: string
}

export type ProcurementReportDefinition = {
  key: string
  title: string
  position: number
  available: boolean
  deferred_reason: string
  can_export: boolean
  visualization: 'table' | 'pivot' | 'calendar'
  pivot_row: string
  pivot_column: string
  pivot_value: string
  columns: ProcurementReportColumn[]
  filters: ProcurementReportFilter[]
}

export type ProcurementReportCatalog = { reports: ProcurementReportDefinition[] }

/**
 * One ordered report line. Values are keyed by column key; numbers are exact
 * decimal strings. Summary rows carry a localized label that belongs in the
 * first empty text cell.
 */
export type ProcurementReportRow = {
  type: ProcurementReportRowType
  label: string
  values: Record<string, string | null>
}

export type ProcurementReportPage = {
  report: ProcurementReportDefinition
  items: ProcurementReportRow[]
  count: number
  page: number
  limit: number
}

/** Entity and enum filters take value lists; the date filter takes YYYY-MM-DD. */
export type ProcurementReportFilters = Record<string, string[] | string>

export type ProcurementReportFilterOption = { value: string; label: string }
export type ProcurementReportFilterOptions = {
  report: string
  filter: string
  options: ProcurementReportFilterOption[]
}

export type Currency = Translated &
  Timestamps & {
    id: string
    company_id: string
    code: string
    icon: string
    is_base: boolean
    is_active: boolean
  }

// ---------- finance / warehouse ----------

export type Cashbox = Timestamps & {
  id: string
  name: string
  is_active: boolean
  branch: Translated & { id: string; code: string }
}

export type Warehouse = Timestamps & {
  id: string
  name: string
  is_active: boolean
  branch_id: string
  branch_name_en: string
  branch_name_ru: string
  branch_name_uz: string
  branch_name_cyr: string
  location: string
  coordinates: string
  /** Profiles working at the warehouse; loaded with the record, saved through its own endpoint. */
  employee_ids?: string[]
  /** HR personnel assignments used by receiving documents. */
  hr_employee_ids?: string[]
}

export type WarehouseHREmployee = { id: string; warehouse_id: string; employee_id: string; display_name: string; employee_number: string; status: 'working' | 'fired' }

export type WarehouseEmployee = {
  id: string
  warehouse_id: string
  profile_id: string
  display_name: string
  role_id: string
}

export type WarehouseCell = {
  id: string
  warehouse_id: string
  warehouse_name: string
  cell_number: string
  cell_date: string
  transport_unit_id: string
  lot_count: number
  available_weight: string
  created_at: string
}

export type WarehouseRegisterFileType = 'controller' | 'reestr' | 'other'

export type WarehouseRegisterFile = {
  id: string
  type: WarehouseRegisterFileType
  file: string
}

/** Ucode's «Реестр»: a warehouse's scanned controller and register sheets for a period. */
export type WarehouseRegister = {
  id: string
  number: string
  warehouse_id: string
  branch_id: string
  from_date: string | null
  to_date: string | null
  from_date_display: string
  to_date_display: string
  header_date_display: string
  files_count: number
  files: WarehouseRegisterFile[]
  created_at: string
  updated_at: string
}

export type WarehouseLotOrigin = 'receipt' | 'opening' | 'deficit' | 'transfer' | 'return' | 'surplus'

export type WarehouseLot = {
  id: string
  warehouse_id: string
  warehouse_name: string
  product_id: string
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  packaging_id: string
  batch_id: string
  batch_number: string
  proforma_id: string
  proforma_number: string
  transport_unit_id: string
  cell_id: string
  cell_number: string
  received_at: string
  weight: string
  count: string
  front_count: string
  rear_count: string
  available_weight: string
  available_count: string
  available_front: string
  available_rear: string
  origin: WarehouseLotOrigin
  consumed_at?: string
  cell_label: string
  is_transfer_copy: boolean
}

export type WarehouseDocumentKind = 'receiving' | 'branch_receiving' | 'shipment' | 'return' | 'transfer' | 'inventory' | 'correction' | 'opening'

/** One signed change of a FIFO lot by a warehouse document line. */
export type WarehouseLotMovement = {
  id: string
  lot_id: string
  document_id: string
  document_kind: WarehouseDocumentKind
  document_number: number
  document_date: string
  line_id: string
  weight: string
  count: string
  front_count: string
  rear_count: string
  created_at: string
}

export type WarehouseDocumentLine = {
  id: string
  line_number: number
  direction: 'in' | 'out'
  warehouse_id: string
  product_id: string
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  is_belarus_meat: boolean
  is_uncut_meat: boolean
  packaging_id?: string
  truck_packaging_id?: string
  weight: string
  count: string
  front_count: string
  rear_count: string
  truck_weight: string
  truck_count: string
  truck_front: string
  truck_rear: string
  system_weight: string
  system_count: string
  system_front: string
  system_rear: string
  batch_id?: string
  batch_number?: string
  proforma_id?: string
  transport_content_id?: string
  /** The line's origin: Batch invoice, Proforma and, for goods moved from a container («Перевалка»), that container. */
  invoice_number?: string
  proforma_number?: string
  origin_container_number?: string
  cell_id?: string
  lot_id?: string
  corrects_line_id?: string
  /** «Заморозка»: the frozen product an «out» line becomes, and its source (truck cell number or «/S» label). */
  target_product_id?: string
  target_product_name_en?: string
  target_product_name_ru?: string
  target_product_name_uz?: string
  target_product_name_cyr?: string
  source_label?: string
}

/** Fresh Belarus meat a «Заморозка» may take: a truck cell's stock of one product (cell_id) or one «/S» row (lot_id). */
export type WarehouseFreezingSource = {
  cell_id: string
  lot_id: string
  label: string
  product_id: string
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  batch_number: string
  weight: string
  front_count: string
  rear_count: string
}

/** A position as a history event recorded it. */
export type WarehouseHistoryItem = {
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  packaging_id?: string
  front: string
  rear: string
  count: string
  weight: string
}

/** One changed header field (kind "header") or position (kind "item"). */
export type WarehouseHistoryChange = {
  kind: 'header' | 'item'
  action: 'create' | 'update' | 'delete'
  field?: string
  old_value?: string
  new_value?: string
  old_item?: WarehouseHistoryItem
  new_item?: WarehouseHistoryItem
}

/** One event of a warehouse document's history, newest first. */
export type WarehouseDocumentHistory = {
  id: string
  event_type: 'created' | 'updated' | 'posted' | 'unposted' | 'corrected' | 'commented' | 'printed'
  actor_profile_id?: string
  actor_name?: string
  actor_role_name_en?: string
  actor_role_name_ru?: string
  actor_role_name_uz?: string
  actor_role_name_cyr?: string
  created_at: string
  changes: WarehouseHistoryChange[]
}

export type WarehouseDocument = {
  id: string
  kind: 'receiving' | 'transfer' | 'inventory' | 'freezing' | 'correction'
  document_number: number
  document_date: string
  status: string
  is_confirmed: boolean
  warehouse_id: string
  warehouse_name: string
  target_warehouse_id?: string
  target_warehouse_name?: string
  cell_id?: string
  cell_number?: string
  transport_unit_id?: string
  tractor_number?: string
  trailer_number?: string
  truck_series?: string
  batch_id?: string
  batch_number?: string
  proforma_id?: string
  proforma_number?: string
  seller_name?: string
  factory_name?: string
  buyer_name?: string
  receiver_employee_id?: string
  receiver_profile_id?: string
  receiver_name?: string
  driver_profile_id?: string
  driver_name?: string
  vehicle_number?: string
  total_weight: string
  total_count: string
  total_front: string
  total_rear: string
  truck_weight: string
  truck_count: string
  truck_front: string
  truck_rear: string
  comment: string
  version: number
  created_by_profile_id?: string
  created_by_name?: string
  created_at: string
  updated_at: string
  lines?: WarehouseDocumentLine[]
}

export type WarehouseReceivableTruck = {
  id: string
  trailer_number: string
  tractor_number: string
  source_type: 'direct' | 'movement'
  proforma_id: string
  proforma_number: string
  batch_numbers: string
  seller_name: string
  factory_name: string
  buyer_name: string
  total_weight: string
}

export type WarehouseTruckLine = {
  transport_content_id: string
  product_id: string
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  is_belarus_meat: boolean
  is_uncut_meat: boolean
  packaging_id?: string
  batch_id: string
  batch_number: string
  proforma_id: string
  /** The line's origin: Batch invoice, Proforma and, for goods moved from a container («Перевалка»), that container. */
  invoice_number?: string
  proforma_number?: string
  origin_container_number?: string
  weight: string
  count: string
  front_count: string
  rear_count: string
}

export type WarehouseStockOption = {
  product_id: string
  product_name_en: string
  product_name_ru: string
  product_name_uz: string
  product_name_cyr: string
  is_belarus_meat: boolean
  is_uncut_meat: boolean
  lot_id?: string
  lot_label?: string
  weight: string
  count: string
  front_count: string
  rear_count: string
}

// ---------- HR ----------

export type JobTitle = Translated & Timestamps & { id: string; status: ActiveStatus }
export type Department = Translated & Timestamps & { id: string; status: ActiveStatus; branch_ids: string[] }
export type WorkLocation = Translated & Timestamps & { id: string; status: ActiveStatus; branch_ids: string[] }

// ---------- files / system ----------

export type UploadedFile = { key: string; name: string; url: string; size: number; content_type: string }
export type HealthResponse = { status: string; app: string; environment: string }

/**
 * One main Proforma status of a transport unit: the planned completion date
 * (reaching its last substatus, when the stage's payables accrue) and the
 * actual one. delay_days is reached − planned once reached, or today −
 * planned while an unreached plan is overdue.
 */
export type ProcurementTransportUnitStatusPlan = {
  proforma_status_id: string
  stage_code: string
  order_number: number
  percentage: string
  planned_date: string | null
  set_by_profile_id: string
  plan_updated_at: string | null
  is_current: boolean
  is_reached: boolean
  reached_at: string | null
  delay_days: number | null
}

export type ProcurementPlannerState = 'planned' | 'overdue'
export type ProcurementPlannerType = 'logistics' | 'goods' | 'customs' | 'goods_reminder' | 'bl_reminder'

/**
 * One payable of the payment calendar, on the planned date of its stage;
 * amounts are exact decimal strings. A completed item is the posted accrual,
 * `actual_date` its accrual date.
 */
export type ProcurementPlannerItem = {
  key: string
  date: string
  state: ProcurementPlannerState
  type: ProcurementPlannerType
  payee_id: string
  payee_name: string
  amount: string
  currency_id: string
  currency_code: string
  /** Empty when the Batch has no rate for the currency. */
  amount_uzs: string
  transport_unit_id: string
  unit_number: string
  unit_type: ProcurementUnitType
  batch_id: string
  batch_number: string
  proforma_id: string
  proforma_number: string
  proforma_status_id: string
  stage_code: string
  stage_number: number
  planned_date: string | null
  completed: boolean
  actual_date: string | null
  debt_id: string
}

export type ProcurementPlannerTotal = { currency_id: string; currency_code: string; amount: string; amount_uzs: string }

export type ProcurementPlannerDay = { date: string; totals: ProcurementPlannerTotal[]; total_uzs: string; count: number }

export type ProcurementPlanner = {
  from: string
  to: string
  today: string
  items: ProcurementPlannerItem[]
  days: ProcurementPlannerDay[]
  totals: ProcurementPlannerTotal[]
  total_uzs: string
  state_totals: { state: ProcurementPlannerState; total_uzs: string; count: number }[]
}
